import React, { useCallback } from "react";
import { useField } from "formik";
import { Entity } from "@common/types/entity";
import {
    useWorkspaceAccountGroups,
    useWorkspaceAccounts,
    useWorkspaceAccrualAccounts,
} from "@api/account.api";
import { useCategoriesById } from "@hooks/useCategoriesById";
import { useEntities, useEntitiesById } from "@hooks/useEntities";
import { useShowAccountNumbers } from "@hooks/useShowAccountNumbers";
import { EntitySelect } from "@components/entity/EntitySelect";
import type { TriggerButtonProps } from "@design-system/overlay/triggerButtonProps";
import {
    TransactionRuleActionFormSchema,
    TransactionRuleConditionFormSchema,
    FormRuleActionType,
    FormRuleConditionType,
} from "../constants";
import { getEffectiveEntityIds } from "../entityScope";
import { getCategoryVisibility } from "../useEntityCategoryVisibility";
import { clearInvalidAccountActionForEntitySelection } from "../RuleConditionsForm/accountActionEntityHelpers";

interface Props {
    index: number;
    controlButtonSize?: TriggerButtonProps["size"];
}

export const EntityActionForm: React.FC<Props> = ({
    index,
    controlButtonSize,
}) => {
    const [field] = useField<TransactionRuleActionFormSchema["value"]>(
        `actions.${index}.value`,
    );
    const entities = useEntities();
    const entitiesById = useEntitiesById({ excludeMockEntities: true });
    const categoriesById = useCategoriesById();
    const accounts = useWorkspaceAccounts();
    const accrualAccounts = useWorkspaceAccrualAccounts();
    const accountGroups = useWorkspaceAccountGroups();
    const showAccountNumbers = useShowAccountNumbers();
    const [actions, , actionsHelpers] =
        useField<TransactionRuleActionFormSchema[]>("actions");
    const [conditions] =
        useField<TransactionRuleConditionFormSchema[]>("conditions");

    const selectedEntity =
        field.value?.entityId !== undefined
            ? entitiesById.get(field.value.entityId)
            : undefined;

    const onChange = useCallback(
        (entityId: number | undefined) => {
            const entityCondition = conditions.value.find(
                (c) => c.conditionType === FormRuleConditionType.ENTITY,
            );

            const { showBusiness, showPersonal } = getCategoryVisibility({
                entityIds: entityCondition?.entityValue ?? [],
                entityActionEntityId: entityId,
                entitiesById,
                context: "actions",
            });

            const updatedActions = actions.value.map((action) => {
                if (action.actionType === FormRuleActionType.ENTITY) {
                    return {
                        ...action,
                        value: { entityId },
                    };
                }

                if (action.actionType === FormRuleActionType.CATEGORY) {
                    const category = action.value?.categoryId
                        ? categoriesById[action.value.categoryId]
                        : undefined;

                    if (
                        (category?.isBusiness && !showBusiness) ||
                        (category && !category.isBusiness && !showPersonal)
                    ) {
                        return {
                            ...action,
                            value: { ...action.value, categoryId: undefined },
                        };
                    }
                }

                return action;
            });

            const targetEntityIds =
                entityId !== undefined
                    ? [entityId]
                    : getEffectiveEntityIds({ conditions: conditions.value });

            const nextActions = clearInvalidAccountActionForEntitySelection({
                actions: updatedActions,
                entityIds: targetEntityIds,
                accounts: accounts.data,
                accrualAccounts: accrualAccounts.data,
                groupsByEntityId: accountGroups.data,
                showAccountNumbers,
            });

            if (nextActions.some((a, i) => a !== actions.value[i])) {
                actionsHelpers.setValue(nextActions);
            }
        },
        [
            actions.value,
            actionsHelpers,
            conditions.value,
            categoriesById,
            entitiesById,
            accounts.data,
            accrualAccounts.data,
            accountGroups.data,
            showAccountNumbers,
        ],
    );

    const handleEntityChange = useCallback(
        (entity: Entity | undefined) => onChange(entity?.id),
        [onChange],
    );

    return (
        <EntitySelect
            selectedEntity={selectedEntity}
            entities={entities}
            onChange={handleEntityChange}
            triggerButtonProps={{
                variant: "primary",
                size: controlButtonSize ?? "md",
            }}
        />
    );
};
